// client/src/components/reports/ReportResult.jsx
//
// One page of report results: the visualization (KPI tiles, bar or line chart
// via recharts) above a table, plus paging. Values arrive already formatted
// for the tenant's time zone by the server; numbers are formatted here.

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { NUMERIC, chartSeries, formatCell } from './reportModel';

// Navy, darker brand orange (contrast-safe on white), then distinct hues.
const SERIES_COLORS = ['#253866', '#c94f00', '#2f855a', '#7c3aed', '#0e7490', '#b45309'];

function Kpis({ columns, rows, viz }) {
  const cols = (viz.y || []).map((k) => columns.find((c) => c.key === k)).filter(Boolean);
  const row = rows[0] || {};
  return (
    <div className="perf-kpis">
      {cols.map((c) => (
        <div key={c.key} className="perf-kpi">
          <div className="perf-kpi-value">{formatCell(c, row[c.key])}</div>
          <div className="perf-kpi-label">{c.label}</div>
        </div>
      ))}
    </div>
  );
}

function Chart({ columns, rows, viz, t }) {
  const { data, series } = chartSeries(columns, rows, viz);
  if (!data.length || !series.length) return null;
  const Chart = viz.type === 'line' ? LineChart : BarChart;
  return (
    <div className="rp-chart" role="img" aria-label={t('reports.result.chartLabel', 'Chart of the report results')}>
      <ResponsiveContainer width="100%" height="100%">
        <Chart data={data} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="x" tick={{ fontSize: 11, fill: '#4b5563' }} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 11, fill: '#4b5563' }} width={48} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {series.map((s, i) => (viz.type === 'line'
            ? <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={SERIES_COLORS[i]} strokeWidth={2} dot={false} isAnimationActive={false} />
            : <Bar key={s.key} dataKey={s.key} name={s.label} fill={SERIES_COLORS[i]} isAnimationActive={false} />))}
        </Chart>
      </ResponsiveContainer>
    </div>
  );
}

// kpiOnly: a one-row KPI result shows just the tiles (board reports), not the
// same numbers again as a table.
export default function ReportResult({ result, onPage, kpiOnly = false }) {
  const { t } = useTranslation();
  if (!result) return null;
  const { columns, rows, visualization: viz = { type: 'table' }, page, hasMore, truncated, rowCap } = result;
  const anyMasked = columns.some((c) => c.masked);

  return (
    <div className="rp-preview">
      {rows.length > 0 && viz.type === 'kpi' && <Kpis columns={columns} rows={rows} viz={viz} />}
      {rows.length > 0 && (viz.type === 'bar' || viz.type === 'line') && <Chart columns={columns} rows={rows} viz={viz} t={t} />}

      {kpiOnly && viz.type === 'kpi' && rows.length === 1 ? null : rows.length === 0 ? (
        <p className="surface-muted">{t('reports.result.empty', 'No rows match this report.')}</p>
      ) : (
        <div className="perf-table-wrap">
          <table className="perf-table rp-table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key} scope="col" className={NUMERIC.includes(c.type) ? 'num' : undefined}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={i}>
                  {columns.map((c) => (
                    <td key={c.key} className={NUMERIC.includes(c.type) ? 'num' : undefined} title={typeof r[c.key] === 'string' ? r[c.key] : undefined}>
                      {formatCell(c, r[c.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(page > 1 || hasMore) && onPage && (
        <div className="rp-pager">
          <button type="button" className="lm-btn secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>
            {t('reports.result.prev', 'Previous')}
          </button>
          <span>{t('reports.result.page', 'Page {{page}}', { page })}</span>
          <button type="button" className="lm-btn secondary" disabled={!hasMore} onClick={() => onPage(page + 1)}>
            {t('reports.result.next', 'Next')}
          </button>
        </div>
      )}
      {truncated && (
        <div className="note warn">
          {t('reports.result.truncated', 'Showing the first {{cap}} rows. Export as CSV for the full result.', { cap: Number(rowCap).toLocaleString() })}
        </div>
      )}
      {anyMasked && (
        <div className="note info">{t('reports.result.masked', 'Some personal details are masked for your role.')}</div>
      )}
    </div>
  );
}
